<template>
  <div
    v-if="showing == 'web_awesome' && icon"
    id="code-snippet-web_awesome"
    role="tabpanel"
    aria-labelledby="tab-web_awesome"
  >
    <wa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Copy Code Snippet"
      custom-copy-icon="faClone"
      copy-tracking-event="icon_web_awesome_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family }"
      lang="markup"
      :code="generatedCodeSnippet"
    />

    <icon-code-snippet-links
      :links="allWebAwesomeLinks"
      :selected-style="styling.style"
      :major-version="majorVersion"
    />
  </div>
</template>

<script>
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'
  import { webAwesomeLearnAboutLinks, WEB_AWESOME_ICON_DOCS_URL } from 'app/views/icons/routing.js'

  // Font Awesome to Web Awesome style mappings
  const SIZE_MAP = {
    '2xs': '0.625em',
    'xs': '0.75em',
    'sm': '0.875em',
    'lg': '1.25em',
    'xl': '1.5em',
    '2xl': '2em',
    '1x': '1em',
    '2x': '2em',
    '3x': '3em',
    '4x': '4em',
    '5x': '5em',
    '6x': '6em',
    '7x': '7em',
    '8x': '8em',
    '9x': '9em',
    '10x': '10em'
  }

  const ROTATION_MAP = {
    'rotate-90': 'transform: rotate(90deg);',
    'rotate-180': 'transform: rotate(180deg);',
    'rotate-270': 'transform: rotate(270deg);',
    'flip-horizontal': 'transform: scaleX(-1);',
    'flip-vertical': 'transform: scaleY(-1);',
    'flip-both': 'transform: scale(-1);'
  }

  const ANIMATION_MAP = {
    'spin': 'animation: fa-spin 2s infinite linear;',
    'spin-reverse': 'animation: fa-spin 2s infinite linear reverse;',
    'pulse': 'animation: fa-pulse 1s infinite steps(8);',
    'beat': 'animation: fa-beat 1s infinite ease-in-out;',
    'fade': 'animation: fa-fade 1s infinite ease-in-out;',
    'beat-fade': 'animation: fa-beat-fade 1s infinite ease-in-out;',
    'bounce': 'animation: fa-bounce 1s infinite;',
    'flip': 'animation: fa-flip 1s infinite ease-in-out;',
    'shake': 'animation: fa-shake 1s infinite;'
  }

  export default {
    name: 'IconCodeSnippetWebAwesome',

    components: {
      IconCodeSnippetLinks
    },

    inject: {
      isWideCustomIcon: {
        default: () => false
      }
    },

    props: {
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      isDuotone: {
        type: Boolean
      },
      showing: {
        required: false
      },
      styling: {
        type: Object
      },
      majorVersion: {
        type: Number
      }
    },

    data() {
      return {}
    },

    computed: {
      generatedCodeSnippet() {
        return `<wa-icon name="${this.icon.name}"${this.webAwesomeFamilyAndVariant}${this.webAwesomeStyles}></wa-icon>`
      },

      allWebAwesomeLinks() {
        const baseLinks = [
          {
            label: 'Adding Icons',
            path: WEB_AWESOME_ICON_DOCS_URL,
            external: true
          }
        ]

        return webAwesomeLearnAboutLinks({
          links: baseLinks,
          isDuotone: this.isDuotone,
          hasCustomSize: this.hasCustomSize,
          hasCustomColor: this.hasCustomColor,
          isWideCustomIcon: this.isWideCustomIcon()
        })
      },

      webAwesomeFamilyAndVariant() {
        const { family, style } = this.styling

        // classic + solid: omit both (defaults)
        if (family === 'classic' && style === 'solid') {
          return ''
        } else if (family === 'classic') {
          // classic + other variant: omit family, show variant
          return ` variant="${style}"`
        } else if (family === 'brands') {
          // brands: show family only, no variant
          return ` family="${family}"`
        } else {
          // other families: show both
          return ` family="${family}" variant="${style}"`
        }
      },

      hasCustomSize() {
        return !!this.styling?.size && this.styling.size !== this.defaultStyles.size
      },

      hasCustomColor() {
        const { colorPrimary, colorSecondary, opacityPrimary, opacitySecondary } = this.styling
        const defaults = this.defaultStyles

        return (
          colorPrimary !== defaults.colorPrimary ||
          colorSecondary !== defaults.colorSecondary ||
          opacityPrimary !== defaults.opacityPrimary ||
          opacitySecondary !== defaults.opacitySecondary
        )
      },

      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      // map Font Awesome size classes to font-size values
      fontSize() {
        if (!this.styling?.size) {
          return ''
        } else {
          return SIZE_MAP[this.styling.size] || ''
        }
      },

      // map Font Awesome rotation to CSS transform
      rotationStyle() {
        if (!this.styling?.rotation) {
          return ''
        } else if (this.hasCustomRotation) {
          return `transform: rotate(${this.styling.rotation}deg);`
        } else {
          return ROTATION_MAP[this.styling.rotation] || ''
        }
      },

      // map Font Awesome animations to CSS animations
      animationStyle() {
        if (!this.styling?.animation) {
          return ''
        } else {
          return ANIMATION_MAP[this.styling.animation] || ''
        }
      },

      // handle duotone and regular colors
      colorStyles() {
        const styles = []
        const { colorPrimary, colorSecondary, opacityPrimary, opacitySecondary } = this.styling
        const defaults = this.defaultStyles

        if (this.isDuotone) {
          // duotone uses CSS custom properties
          if (colorPrimary !== defaults.colorPrimary) styles.push(`--primary-color: ${colorPrimary};`)
          if (colorSecondary !== defaults.colorSecondary) styles.push(`--secondary-color: ${colorSecondary};`)
          if (opacityPrimary !== defaults.opacityPrimary) styles.push(`--primary-opacity: ${opacityPrimary};`)
          if (opacitySecondary !== defaults.opacitySecondary) styles.push(`--secondary-opacity: ${opacitySecondary};`)
        } else if (colorPrimary !== defaults.colorPrimary) {
          // regular icons use color property
          styles.push(`color: ${colorPrimary};`)
        }

        return styles.join(' ')
      },

      // combine all styles into a single style attribute
      webAwesomeStyles() {
        const styles = [this.fontSize && `font-size: ${this.fontSize};`, this.rotationStyle, this.animationStyle, this.colorStyles].filter(Boolean)

        return styles.length > 0 ? ` style="${styles.join(' ')}"` : ''
      }
    }
  }
</script>
